// The notification bell in the header (TGCLOUD-1896). It is on every page, so
// that what is outstanding can still be seen away from Home, and it shows the
// same list as the Notifications panel there: both read the one home feed
// (useNotificationFeed), so the badge and the panel's count are the same
// number. They used to be fetched apart, filtered apart and described apart,
// and showed 12 and 5 for the same user at the same moment.
import React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeContext } from 'src/store/ContextStore';
import { NOTE_TYPE_COLOURS } from 'src/components/Constants';
import { useAutoTranslation, useLanguage } from 'src/hooks/useTranslate';
import useNotificationFeed, {
  useDismissNotification
} from 'src/hooks/useNotificationFeed';
import { describeNotification, recordPathOf } from 'src/util/notificationFeed';

const NOTIFICATION_MENU_ID = 'primary-notifications-menu';

const StyledMenu = styled(Menu, {
  shouldForwardProp: (prop) => prop !== 'appearance'
})(({ appearance }) => ({
  '& .MuiPaper-root': {
    backgroundColor: appearance?.color,
    color: appearance?.text,
    boxShadow: `3px 3px 15px ${appearance?.shade}`
  }
}));

/**
 * @param {{ user: { id?: string, _id?: string } }} props
 */
export default function NotificationBell({ user }) {
  const t = useAutoTranslation();
  const lang = useLanguage();
  const { theme } = React.useContext(ThemeContext);
  const appearance = theme?.appearance;

  // `_id` first, as Home hands the panel its user, so the two share one cache.
  const myId = String(user?._id || user?.id || '');
  const feed = useNotificationFeed({ userId: myId });
  const dismissItem = useDismissNotification(myId);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showAll, setShowAll] = React.useState(true);

  const items = React.useMemo(() => {
    const all = feed.items || [];
    if (showAll || !myId) return all;
    return all.filter((n) => !n.createdBy || String(n.createdBy) === myId);
  }, [feed.items, showAll, myId]);

  const open = (event) => {
    setAnchorEl(event.currentTarget);
    // Opening the bell is asking what is outstanding now.
    feed.refetch();
  };
  const close = () => setAnchorEl(null);

  const openRecord = (n) => {
    const path = recordPathOf(n);
    if (!path) return;
    window.location.href = path;
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        color="inherit"
        data-va-anchor="topbar.notifications"
        aria-label={t('Notifications')}
        aria-controls={NOTIFICATION_MENU_ID}
        onClick={open}
        size="large"
      >
        <Badge badgeContent={items.length} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <StyledMenu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        id={NOTIFICATION_MENU_ID}
        open={Boolean(anchorEl)}
        onClose={close}
        appearance={appearance}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 16px',
            gap: 12
          }}
        >
          <div style={{ flex: 1 }} />
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {showAll ? t('All Notifications') : t('My Notifications')}
          </Typography>
          <Switch
            size="small"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            slotProps={{
              input: {
                'aria-label': showAll
                  ? t('Show all notifications')
                  : t('Show only my notifications')
              }
            }}
          />
        </div>
        <Divider />

        {items.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2">
              {t('No upcoming reminders.')}
            </Typography>
          </MenuItem>
        )}

        {items.map((n) => {
          const color = NOTE_TYPE_COLOURS[n.type] || '#888';
          // The record types are the feed's own fixed names.
          const typeLabel = t(n.type);
          const secondary = [
            n.familyMemberId || n.familyId
              ? (n.familyMemberId || n.familyId).replace(/-\d+$/, '')
              : '',
            [n.firstName, n.lastName].filter(Boolean).join(' ')
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Box
              key={`${n.type}_${n.id}`}
              data-testid="bell-notification"
              onClick={() => openRecord(n)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.5,
                py: 1,
                borderLeft: `3px solid ${color}`,
                cursor: 'pointer',
                bgcolor: alpha(color, 0.08),
                '&:hover': { bgcolor: alpha(color, 0.14) },
                '& + .MuiBox-root': { borderTop: '1px solid #f0f0ee' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color,
                    lineHeight: 1
                  }}
                >
                  {typeLabel}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, fontWeight: 500, lineHeight: 1.3 }}
                >
                  {describeNotification(n, t, lang)}
                </Typography>
                {secondary && (
                  <Typography sx={{ fontSize: 12, color: '#888', mt: 0.25 }}>
                    {secondary}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissItem(n);
                }}
                aria-label={t('Dismiss {{type}} notification', {
                  type: typeLabel
                })}
                sx={{ mt: -0.5 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        })}
      </StyledMenu>
    </>
  );
}
